package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.render.model.ImageStyleMode
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import kotlin.jvm.JvmStatic

/**
 * 按钮控件。M3 起使用 `onClick(UiEventHandler<MouseEvent>)` 注册点击 handler。默认 `focusable=true`。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class Button private constructor() : UIElement<Button>() {

    private var colorField = 0xFFFFFFFF.toInt()
    private var textField = ""
    private var variantField = ButtonVariant.FILLED

    /** M6-f §8.1：是否响应点击。false 时 IrInterpreter 在 onClick trigger 前直接返回，不执行 DSL 也不播放动画。 */
    private var enabledField = true

    // Stage 2：DSL 可选状态视觉覆盖（null = 走主题默认）
    private var hoverOverlayColorField: Int? = null
    private var hoverOverlayOpacityField: Float? = null
    private var pressedOverlayColorField: Int? = null
    private var pressedOverlayOpacityField: Float? = null
    private var focusOverlayColorField: Int? = null
    private var focusOverlayOpacityField: Float? = null
    private var focusBorderColorField: Int? = null
    private var disabledOpacityField: Float? = null

    // Stage 3a：DSL 可选几何覆盖（null = 走主题默认）
    private var textPaddingYField: Float? = null

    /** 圆角容器（任意角圆角）。null 表示未指定，由 ShapeResolver 链路决定；与旧 cornerRadius(Float) 双向兼容。 */
    private var cornerRadiiField: CornerRadii? = null
    private var iconField: String? = null
    private var iconSizeField: Float? = null
    private var iconPositionField: String? = null

    // 图标与文字相对默认布局位置的额外偏移，便于精细微调
    private var iconOffsetXField: Float? = null
    private var iconOffsetYField: Float? = null
    private var textOffsetXField: Float? = null
    private var textOffsetYField: Float? = null
    private var elevationField: Float? = null

    // RFC Button Stage 1：纹理背景与九宫格切片
    private var backgroundTextureField: String? = null
    private var hoverBackgroundTextureField: String? = null
    private var pressedBackgroundTextureField: String? = null
    private var focusBackgroundTextureField: String? = null
    private var disabledBackgroundTextureField: String? = null
    private var textureUvXField: Float? = null
    private var textureUvYField: Float? = null
    private var textureUvWField: Float? = null
    private var textureUvHField: Float? = null

    // 状态相关 UV-Y 覆盖：vanilla widgets.png「同一张图按 Y 切换 normal/highlighted/disabled」用法
    private var hoverTextureUvYField: Float? = null
    private var pressedTextureUvYField: Float? = null
    private var focusTextureUvYField: Float? = null
    private var disabledTextureUvYField: Float? = null
    private var sliceTopField: Float? = null
    private var sliceRightField: Float? = null
    private var sliceBottomField: Float? = null
    private var sliceLeftField: Float? = null
    private var textureModeField = ImageStyleMode.NORMAL

    // RFC Button Stage 2：渐变背景。CSS 角度：0°=下→上，90°=左→右，180°=上→下，270°=右→左。
    private var gradientTypeField: String? = null
    private var gradientAngleField: Float? = null
    private var gradientStartColorField: Int? = null
    private var gradientEndColorField: Int? = null
    private var gradientStartStopField: Float? = null
    private var gradientEndStopField: Float? = null
    private var gradientCenterXField: Float? = null
    private var gradientCenterYField: Float? = null
    private var gradientRadiusField: Float? = null

    // Stage 4：Loading 状态
    private var loadingField = false
    private var loadingTextField: String? = null
    private var loadingSpinnerKindField: String? = null

    /** 内部缓存：进入 loading 时保存当前 enabled 状态，退出时恢复。不暴露给 DSL。 */
    private var prevEnabledSnapshotField: Boolean? = null

    // Stage 6：多行文本
    private var multilineField = false
    private var textOverflowField = "wrap"
    private var maxLinesField: Int? = null
    private var lineSpacingField: Float? = null
    private var textAlignField = "center"
    private var verticalAlignField = "middle"

    // Stage 7：DSL size 倍率（内部文本字号倍率，默认 1f）
    private var textScaleField = 1f

    // 交互态逐实例覆盖（null = 回退渲染器常量 / 主题）
    private var glowIntensityScaleField: Float? = null
    private var glowSizeBoostField: Float? = null
    private var hoverTextBrightenField: Float? = null
    private var textTransformField: String? = null

    // Stage 3b：双击事件
    private var onDoubleClickField: String? = null
    private var onDoubleClickHandlerField: UiEventHandler<MouseEvent>? = null

    init {
        focusableField = true
    }

    override fun self(): Button = this

    fun text(text: String?): Button = apply { textField = text ?: "" }

    fun color(color: Int): Button = apply { colorField = color }

    /**
     * 把按钮文本绑定到一个动态供给器：依赖变化时自动更新文本。
     * 内部建立的 [Effect] 注册到本元素的 [lifecycleScope]，元素 dispose 时一并释放。
     */
    fun bindText(supplier: () -> String): Button = apply {
        lifecycleScopeField.register(Effect { textField = supplier() })
    }

    fun text(): String = textField

    fun color(): Int = colorField

    fun variant(variant: ButtonVariant?): Button = apply { variantField = variant ?: ButtonVariant.FILLED }
    fun variant(variant: String?): Button = apply { variantField = ButtonVariant.fromString(variant) }
    fun variant(): ButtonVariant = variantField

    /** 设置是否启用。 */
    fun enabled(v: Boolean): Button = apply { enabledField = v }
    override fun enabled(): Boolean = enabledField

    /** hotkey 派发资格：在基类 visible 要求之上，额外要求 enabled 且非 loading（原 companion `is Button` 特判下沉）。 */
    override fun hotkeyEligible(): Boolean = super.hotkeyEligible() && enabled() && !loading()

    // === Stage 2：状态视觉覆盖 ===

    fun hoverOverlayColor(v: Int?): Button = apply { hoverOverlayColorField = v }
    fun hoverOverlayColor(): Int? = hoverOverlayColorField

    fun hoverOverlayOpacity(v: Float?): Button = apply { hoverOverlayOpacityField = v }
    fun hoverOverlayOpacity(): Float? = hoverOverlayOpacityField

    fun pressedOverlayColor(v: Int?): Button = apply { pressedOverlayColorField = v }
    fun pressedOverlayColor(): Int? = pressedOverlayColorField

    fun pressedOverlayOpacity(v: Float?): Button = apply { pressedOverlayOpacityField = v }
    fun pressedOverlayOpacity(): Float? = pressedOverlayOpacityField

    fun focusOverlayColor(v: Int?): Button = apply { focusOverlayColorField = v }
    fun focusOverlayColor(): Int? = focusOverlayColorField

    fun focusOverlayOpacity(v: Float?): Button = apply { focusOverlayOpacityField = v }
    fun focusOverlayOpacity(): Float? = focusOverlayOpacityField

    fun focusBorderColor(v: Int?): Button = apply { focusBorderColorField = v }
    fun focusBorderColor(): Int? = focusBorderColorField

    fun disabledOpacity(v: Float?): Button = apply { disabledOpacityField = v }
    fun disabledOpacity(): Float? = disabledOpacityField

    // === Stage 3a：几何覆盖 ===

    /** 文本上下内边距覆盖（像素）。null 表示走主题默认。 */
    fun textPaddingY(v: Float?): Button = apply { textPaddingYField = v }
    fun textPaddingY(): Float? = textPaddingYField

    /** 圆角半径覆盖（像素）。null 表示走主题默认。 */
    fun cornerRadius(v: Float?): Button = apply { cornerRadiiField = v?.let { CornerRadii.uniform(it) } }

    /** 兼容 getter：取 TL；null 时返回 null（沿用旧「未指定」语义）。 */
    fun cornerRadius(): Float? = cornerRadiiField?.tl

    /** 任意角圆角（推荐）。null 表示未指定，走主题/变体兜底。 */
    fun cornerRadii(v: CornerRadii?): Button = apply { cornerRadiiField = v }
    fun cornerRadii(): CornerRadii? = cornerRadiiField

    /** 图标资源路径。null 或空表示无图标。 */
    fun icon(v: String?): Button = apply { iconField = v }
    fun icon(): String? = iconField

    /** 图标尺寸覆盖（像素）。null 表示走主题默认。 */
    fun iconSize(v: Float?): Button = apply { iconSizeField = v }
    fun iconSize(): Float? = iconSizeField

    /** 图标位置（"left" / "right"）。null 表示走默认 "left"。 */
    fun iconPosition(v: String?): Button = apply { iconPositionField = v }
    fun iconPosition(): String? = iconPositionField

    fun iconOffsetX(v: Float?): Button = apply { iconOffsetXField = v }
    fun iconOffsetX(): Float? = iconOffsetXField

    fun iconOffsetY(v: Float?): Button = apply { iconOffsetYField = v }
    fun iconOffsetY(): Float? = iconOffsetYField

    fun textOffsetX(v: Float?): Button = apply { textOffsetXField = v }
    fun textOffsetX(): Float? = textOffsetXField

    fun textOffsetY(v: Float?): Button = apply { textOffsetYField = v }
    fun textOffsetY(): Float? = textOffsetYField

    fun elevation(v: Float?): Button = apply { elevationField = v }
    fun elevation(): Float? = elevationField

    fun backgroundTexture(v: String?): Button = apply { backgroundTextureField = v }
    fun backgroundTexture(): String? = backgroundTextureField

    fun hoverBackgroundTexture(v: String?): Button = apply { hoverBackgroundTextureField = v }
    fun hoverBackgroundTexture(): String? = hoverBackgroundTextureField

    fun pressedBackgroundTexture(v: String?): Button = apply { pressedBackgroundTextureField = v }
    fun pressedBackgroundTexture(): String? = pressedBackgroundTextureField

    fun focusBackgroundTexture(v: String?): Button = apply { focusBackgroundTextureField = v }
    fun focusBackgroundTexture(): String? = focusBackgroundTextureField

    fun disabledBackgroundTexture(v: String?): Button = apply { disabledBackgroundTextureField = v }
    fun disabledBackgroundTexture(): String? = disabledBackgroundTextureField

    fun textureUvX(v: Float?): Button = apply { textureUvXField = v }
    fun textureUvX(): Float? = textureUvXField

    fun textureUvY(v: Float?): Button = apply { textureUvYField = v }
    fun textureUvY(): Float? = textureUvYField

    fun textureUvW(v: Float?): Button = apply { textureUvWField = v }
    fun textureUvW(): Float? = textureUvWField

    fun textureUvH(v: Float?): Button = apply { textureUvHField = v }
    fun textureUvH(): Float? = textureUvHField

    fun hoverTextureUvY(v: Float?): Button = apply { hoverTextureUvYField = v }
    fun hoverTextureUvY(): Float? = hoverTextureUvYField

    fun pressedTextureUvY(v: Float?): Button = apply { pressedTextureUvYField = v }
    fun pressedTextureUvY(): Float? = pressedTextureUvYField

    fun focusTextureUvY(v: Float?): Button = apply { focusTextureUvYField = v }
    fun focusTextureUvY(): Float? = focusTextureUvYField

    fun disabledTextureUvY(v: Float?): Button = apply { disabledTextureUvYField = v }
    fun disabledTextureUvY(): Float? = disabledTextureUvYField

    fun sliceTop(v: Float?): Button = apply { sliceTopField = v }
    fun sliceTop(): Float? = sliceTopField

    fun sliceRight(v: Float?): Button = apply { sliceRightField = v }
    fun sliceRight(): Float? = sliceRightField

    fun sliceBottom(v: Float?): Button = apply { sliceBottomField = v }
    fun sliceBottom(): Float? = sliceBottomField

    fun sliceLeft(v: Float?): Button = apply { sliceLeftField = v }
    fun sliceLeft(): Float? = sliceLeftField

    fun textureMode(v: ImageStyleMode?): Button = apply { textureModeField = v ?: ImageStyleMode.NORMAL }
    fun textureMode(): ImageStyleMode = textureModeField

    fun gradientType(v: String?): Button = apply { gradientTypeField = v }
    fun gradientType(): String? = gradientTypeField

    fun gradientAngle(v: Float?): Button = apply { gradientAngleField = v }
    fun gradientAngle(): Float? = gradientAngleField

    fun gradientStartColor(v: Int?): Button = apply { gradientStartColorField = v }
    fun gradientStartColor(): Int? = gradientStartColorField

    fun gradientEndColor(v: Int?): Button = apply { gradientEndColorField = v }
    fun gradientEndColor(): Int? = gradientEndColorField

    fun gradientStartStop(v: Float?): Button = apply { gradientStartStopField = v }
    fun gradientStartStop(): Float? = gradientStartStopField

    fun gradientEndStop(v: Float?): Button = apply { gradientEndStopField = v }
    fun gradientEndStop(): Float? = gradientEndStopField

    fun gradientCenterX(v: Float?): Button = apply { gradientCenterXField = v }
    fun gradientCenterX(): Float? = gradientCenterXField

    fun gradientCenterY(v: Float?): Button = apply { gradientCenterYField = v }
    fun gradientCenterY(): Float? = gradientCenterYField

    fun gradientRadius(v: Float?): Button = apply { gradientRadiusField = v }
    fun gradientRadius(): Float? = gradientRadiusField

    fun hasGradient(): Boolean =
        !gradientTypeField.isNullOrBlank() && gradientStartColorField != null && gradientEndColorField != null

    /** UV 宽高同时为正数时启用 atlas 切片。 */
    fun hasTextureUv(): Boolean = positive(textureUvWField) && positive(textureUvHField)

    /** 任一九宫格边距为正数时启用 9-slice。 */
    fun hasNineSlice(): Boolean =
        positive(sliceTopField) || positive(sliceRightField) || positive(sliceBottomField) || positive(sliceLeftField)

    /** 双击 action 名称。 */
    fun onDoubleClick(actionName: String?): Button = apply { onDoubleClickField = actionName }
    fun onDoubleClick(): String? = onDoubleClickField

    /** 双击 handler。 */
    fun onDoubleClickHandler(handler: UiEventHandler<MouseEvent>?): Button =
        apply { onDoubleClickHandlerField = handler }
    fun onDoubleClickHandler(): UiEventHandler<MouseEvent>? = onDoubleClickHandlerField

    // === Stage 4：Loading 状态 ===

    /**
     * 设置 loading 状态。进入 loading（false→true）时快照当前 enabled 并强制 disabled；
     * 退出 loading（true→false）时恢复快照中的 enabled 值。
     */
    fun loading(v: Boolean): Button = apply {
        if (!loadingField && v) {
            // 进入 loading：保存当前 enabled 快照
            prevEnabledSnapshotField = enabledField
            enabledField = false
        } else if (loadingField && !v) {
            // 退出 loading：恢复 enabled 快照
            enabledField = prevEnabledSnapshotField ?: true
            prevEnabledSnapshotField = null
        }
        loadingField = v
    }

    fun loading(): Boolean = loadingField

    fun loadingText(v: String?): Button = apply { loadingTextField = v }
    fun loadingText(): String? = loadingTextField

    fun loadingSpinnerKind(v: String?): Button = apply { loadingSpinnerKindField = v }
    fun loadingSpinnerKind(): String? = loadingSpinnerKindField

    /** 获取 loading 期间应显示的文本：loadingText 非空时使用它，否则使用原 text。 */
    fun effectiveText(): String {
        val lt = loadingTextField
        return if (loadingField && lt != null) lt else textField
    }

    // === Stage 6：多行文本 ===

    /** 是否启用多行文本。false 时 \n 替换为空格。 */
    fun multiline(v: Boolean): Button = apply { multilineField = v }
    fun multiline(): Boolean = multilineField

    /** 文本溢出处理方式："wrap"/"ellipsis"/"clip"。 */
    fun textOverflow(v: String?): Button = apply { textOverflowField = v ?: "wrap" }
    fun textOverflow(): String = textOverflowField

    /** 最大行数限制。null 表示不限。 */
    fun maxLines(v: Int?): Button = apply { maxLinesField = v }
    fun maxLines(): Int? = maxLinesField

    /** 行间距倍率。null 走 theme 默认（1.2f）。 */
    fun lineSpacing(v: Float?): Button = apply { lineSpacingField = v }
    fun lineSpacing(): Float? = lineSpacingField

    /** 文本水平对齐："left"/"center"/"right"。 */
    fun textAlign(v: String?): Button = apply { textAlignField = v ?: "center" }
    fun textAlign(): String = textAlignField

    /** 文本垂直对齐："top"/"middle"/"bottom"。 */
    fun verticalAlign(v: String?): Button = apply { verticalAlignField = v ?: "middle" }
    fun verticalAlign(): String = verticalAlignField

    /**
     * 内部文本字号倍率。区别于 `Text.size()`：Button 没有 typography baseline 概念，仅代表「按钮内部文字
     * 相对默认 9px 的缩放」。默认 1f。由 DSL `size = 1.5` 写入，ButtonRenderer 在 drawText / textWidth
     * 调用时传给 UiRenderBridge 作 scale 参数。
     */
    fun textScale(v: Float): Button = apply { textScaleField = if (v <= 0f) 1f else v }
    fun textScale(): Float = textScaleField

    // === 交互态逐实例覆盖（null = 回退渲染器常量 / 主题）===

    /** hover/pressed 辉光强度倍率覆盖；null 时回退渲染器各自常量。 */
    fun glowIntensityScale(v: Float?): Button = apply { glowIntensityScaleField = v }
    fun glowIntensityScale(): Float? = glowIntensityScaleField

    /** hover/pressed 辉光扩张像素覆盖；null 时回退渲染器各自常量。 */
    fun glowSizeBoost(v: Float?): Button = apply { glowSizeBoostField = v }
    fun glowSizeBoost(): Float? = glowSizeBoostField

    /** hover 文字提亮比覆盖；null 时回退渲染器常量。 */
    fun hoverTextBrighten(v: Float?): Button = apply { hoverTextBrightenField = v }
    fun hoverTextBrighten(): Float? = hoverTextBrightenField

    /** 文字大小写变换覆盖（"none"/"uppercase"/"lowercase"）；null 时回退主题 buttonTextTransform。 */
    fun textTransform(v: String?): Button = apply { textTransformField = v }
    fun textTransform(): String? = textTransformField

    companion object {
        @JvmStatic
        fun create(): Button = Button()

        private fun positive(v: Float?): Boolean = (v ?: 0f) > 0f
    }
}
